import { useMemo } from "react";
import { IrrigationEvent } from "verdi-common/schemas/irrigationEvent/irrigationEvent";
import { hourMs } from "verdi-common/timeConstants";
import { MasterIndex } from "verdi-web-client/Models";

import { CLOCK_CADENCE, useWallClock } from "@/providers/wall-clock";
import { useIrrigationInstances } from "@/services/irrigation-scheduling/use-irrigation-instances";
import { useIrrigationSets } from "@/services/irrigation-scheduling/use-irrigation-sets";
import { type TimelineItem, type VisualizationItems } from "./event-visualization/event-visualization";

// The visualization should include this much time (in ms) before the current time
export const TIME_BEFORE_TO_INCLUDE_IN_VISUALIZATION_MS = 2 * hourMs;

// The time range that is displayed in the visualization
// The base window is 8 hours
export const BASE_WINDOW_HOURS = 8;
// The maximum extension is 4 hours if any event ends past the initial base window
export const MAX_EXTENSION_HOURS = 4;

const VISUALIZATION_WINDOW_MS = (BASE_WINDOW_HOURS + MAX_EXTENSION_HOURS) * hourMs;

/** One irrigation occurrence for the zone this card is showing, as this zone actually runs it. */
export type OnDemandEvent = {
    recordId: string;
    /** The version of the definition this occurrence was rendered from; refetches update it with the baseline. */
    versionId?: number;
    startTime: Date;
    endTime: Date;
    durationMs: number;
    irrigationSetId?: string;
};

function toTimelineItem(event: OnDemandEvent): TimelineItem {
    return {
        startTime: event.startTime,
        endTime: event.endTime,
        isSetEvent: event.irrigationSetId !== undefined,
        id: event.recordId,
    };
}

/**
 * Handles data and visualization updates for the on-demand irrigation card.
 */
export const useOnDemandEventData = (zoneId: string) => {
    const nowMs = useWallClock(CLOCK_CADENCE.MINUTES_1);
    // Memoized for referential stability: a plain new Date(nowMs) would be a new object every render, breaking downstream useMemos
    const nowDate = useMemo(() => new Date(nowMs), [nowMs]);

    const windowStart = useMemo(() => new Date(nowMs - TIME_BEFORE_TO_INCLUDE_IN_VISUALIZATION_MS), [nowMs]);
    const windowEnd = useMemo(() => new Date(windowStart.getTime() + VISUALIZATION_WINDOW_MS), [windowStart]);

    const zoneIds = useMemo(() => [zoneId], [zoneId]);

    const { instancesByZoneId, definitionsByRecordId } = useIrrigationInstances({
        start: windowStart,
        end: windowEnd,
        zoneIds,
    });

    const { sets } = useIrrigationSets();

    const allEvents = useMemo<OnDemandEvent[]>(
        () =>
            (instancesByZoneId[zoneId] ?? [])
                .map((instance) => ({
                    recordId: instance.recordId,
                    versionId: instance.versionId,
                    startTime: instance.startTime,
                    endTime: new Date(instance.startTime.getTime() + instance.durationMs),
                    durationMs: instance.durationMs,
                    irrigationSetId: instance.irrigationSetId,
                }))
                .sort((a, b) => a.startTime.getTime() - b.startTime.getTime()),
        [instancesByZoneId, zoneId],
    );

    const visualizationItems = useMemo<VisualizationItems[]>(
        () => [
            {
                zoneId,
                zoneName: MasterIndex.zone.byID[zoneId]?.name ?? zoneId,
                events: allEvents.map(toTimelineItem),
            },
        ],
        [zoneId, allEvents],
    );

    // Nearest future event: first in start-time order that hasn't ended
    const nearestFutureEvent = useMemo(
        () => allEvents.find((event) => event.endTime.getTime() > nowDate.getTime()),
        [allEvents, nowDate],
    );

    // True when the nearest visible event belongs to a scheduled set.
    const nearestEventIsSet = nearestFutureEvent?.irrigationSetId !== undefined;

    const nearestEventSetId = nearestFutureEvent?.irrigationSetId;
    const nearestEventSetName = useMemo(
        () => sets.find((set) => set._id === nearestEventSetId)?.name,
        [sets, nearestEventSetId],
    );

    // Why the stop endpoint would refuse this event, so the stop button and the server agree. Only
    // the stop is gated on it; editing goes to a different endpoint with its own preconditions.
    const stopBlockedReason = useMemo(() => {
        const definition = nearestFutureEvent && definitionsByRecordId[nearestFutureEvent.recordId];
        if (!definition) {
            return undefined;
        }
        return IrrigationEvent.utilities.getStopBlockedReason(definition, nowMs);
    }, [nearestFutureEvent, definitionsByRecordId, nowMs]);

    return {
        allEvents,
        visualizationItems,
        nearestFutureEvent,
        nearestEventIsSet,
        nearestEventSetName,
        stopBlockedReason,
        nowDate,
    };
};
